import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowsClockwise, Baseball, Basketball, Trophy, Lightning, GitDiff, Plus, Check, CaretRight, CaretDown, Star, Lock, EnvelopeSimple, ShieldCheck, X, WarningCircle, Compass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import BigRoad from "../components/BigRoad";
import TeamLogo from "../components/TeamLogo";
import { computeAnalystRecord, dollarsFor } from "../lib/analystRecord";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => {
  if (n == null || n === 0) return "—";
  const v = Number(n); const sign = v > 0 ? "+" : "−";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const moneyColor = (n) => (Number(n) > 0 ? "#22C55E" : Number(n) < 0 ? "#FF3B30" : "#9ca3af");

// Local persistence keys for the Lab's filter state.
const LS_KEYS = {
  sport: "lab.sport",
  mode: "lab.mode",
  analyst: "lab.analystFilter",
};
const lsGet = (key, fallback) => {
  try { const v = window.localStorage.getItem(key); return v ?? fallback; }
  catch { return fallback; }
};
const lsSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* noop */ }
};

/**
 * Thin wrapper around computeAnalystRecord that operates on a team payload
 * (with team.results) — kept for callsite ergonomics.
 */
function analystRecord(team, analystFilter) {
  return computeAnalystRecord(team?.results, analystFilter, "diff");
}

function PillGroup({ children }) {
  return <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg">{children}</div>;
}
function Pill({ active, onClick, bg, txt, dimTxt, children, "data-testid": tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 first:border-l-0 ${
        active ? "shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]" : "hover:opacity-90"
      }`}
      style={active ? { background: bg, color: txt } : { background: `${bg}26`, color: dimTxt }}>
      {children}
    </button>
  );
}

function PinnedTile({ team, sport, onRemove, busy, mode, analystFilter }) {
  const { actualW, actualL, theoW, theoL, skipped, skippedW, skippedL } = analystRecord(team, analystFilter);
  const actDollars = dollarsFor(actualW, actualL);
  const theoDollars = dollarsFor(theoW, theoL);
  const analystLabel = team.in_charlie && team.in_dakota ? "Both" : team.in_charlie ? "Charlie" : "Dakota";
  const accent = team.in_charlie && team.in_dakota ? "#F59E0B" : team.in_charlie ? "#007AFF" : "#A855F7";
  const filterTag = analystFilter !== "both" ? (analystFilter === "charlie" ? "C" : "D") : null;
  const filterName = analystFilter === "charlie" ? "Charlie" : analystFilter === "dakota" ? "Dakota" : "any analyst";
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid={`pinned-${sport}-${team.team_abbr}`}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TeamLogo sport={sport} abbr={team.team_abbr} size={28} />
          <div>
            <div className="font-bold">{team.team_name}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{team.team_abbr} · {team.wins}-{team.losses} season</div>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold" style={{ background: `${accent}22`, color: accent }}>{analystLabel}</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono-d">
          <div title={`Actual sent · ${filterName}`}>
            ● {filterTag && <span className="text-zinc-500 mr-0.5">{filterTag}</span>}<span style={{ color: moneyColor(actDollars) }}>{actualW}-{actualL} {fmtMoney(actDollars)}</span>
          </div>
          <div title={`${filterName} qualified bets · ${theoW}W · ${theoL}L · ${skipped} skipped (${skippedW}W would-have-won · ${skippedL}L would-have-lost)`}>
            ◌ {filterTag && <span className="text-zinc-500 mr-0.5">{filterTag}</span>}<span style={{ color: moneyColor(theoDollars) }}>{theoW}-{theoL}-{skipped} {fmtMoney(theoDollars)}</span>
            {skipped > 0 && <span className="ml-1.5 text-zinc-500 text-[10px]">· skip <span className="text-red-400">{skippedW}W</span>·<span className="text-blue-400">{skippedL}L</span></span>}
          </div>
          <button onClick={() => onRemove(team)} disabled={busy} data-testid={`pinned-remove-${team.team_abbr}`}
            className="ml-2 px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded-sm border border-[#22C55E]/50 text-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 disabled:opacity-40">
            <Check size={11} weight="bold" className="inline mr-1" /> Shortlisted
          </button>
        </div>
      </div>
      <BigRoad title="" results={team.results} mode={mode} analystFilter={analystFilter} />
    </div>
  );
}

function UniverseRow({ team, sport, expanded, onToggle, onAdd, busy, mode, analystFilter }) {
  const { actualW, actualL, theoW, theoL, skipped, skippedW, skippedL } = analystRecord(team, analystFilter);
  const actDollars = dollarsFor(actualW, actualL);
  const theoDollars = dollarsFor(theoW, theoL);
  const diffDollars = theoDollars + actDollars; // value of theoretical-not-actual + value of actual sent
  const last20 = (team.results || []).slice(-20);
  return (
    <>
      <tr className="border-b border-white/[0.04] hover:bg-white/[0.025] cursor-pointer" onClick={onToggle} data-testid={`universe-row-${sport}-${team.team_abbr}`}>
        <td className="px-3 py-2 w-7 text-zinc-500">{expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
        <td className="px-2 py-2">
          <div className="flex items-center gap-2">
            <TeamLogo sport={sport} abbr={team.team_abbr} size={20} />
            <div>
              <div className="text-sm font-bold leading-tight">{team.team_abbr}</div>
              <div className="text-[10px] text-zinc-500 leading-tight">{team.team_name}</div>
            </div>
          </div>
        </td>
        <td className="px-2 py-2 font-mono-d text-[11px] text-zinc-300">{team.wins}-{team.losses}</td>
        <td className="px-2 py-2 font-mono-d text-[11px]"><span style={{ color: moneyColor(actDollars) }}>● {actualW}-{actualL} {fmtMoney(actDollars)}</span></td>
        <td className="px-2 py-2 font-mono-d text-[11px]" title={`Qualified bets · ${theoW}W · ${theoL}L · ${skipped} skipped (${skippedW}W would-have-won · ${skippedL}L would-have-lost)`}>
          <span style={{ color: moneyColor(theoDollars) }}>◌ {theoW}-{theoL}-{skipped} {fmtMoney(theoDollars)}</span>
          {skipped > 0 && <span className="ml-1 text-zinc-500 text-[10px]">(<span className="text-red-400">{skippedW}W</span>/<span className="text-blue-400">{skippedL}L</span>)</span>}
        </td>
        <td className="px-2 py-2 font-mono-d text-xs font-bold text-right" style={{ color: moneyColor(diffDollars) }}>{fmtMoney(diffDollars)}</td>
        <td className="px-2 py-2">
          <div className="flex gap-0.5">
            {last20.map((r, i) => {
              const isW = r.result === "W"; const hasPick = (r.actual_analysts || []).length > 0 || (r.theoretical_analysts || []).length > 0;
              const isSkipped = (r.skipped_analysts || []).length > 0;
              return <span key={i} className={`inline-block w-2 h-2 rounded-full ${isW ? "bg-red-600" : "bg-blue-500"}`}
                style={{ boxShadow: hasPick ? "0 0 0 1.5px #fff" : "none", opacity: isSkipped && !hasPick ? 0.35 : 1 }} />;
            })}
          </div>
        </td>
        <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="inline-flex gap-1">
            <button onClick={() => onAdd(team, "charlie")} disabled={busy} data-testid={`add-charlie-${team.team_abbr}`}
              className="px-2 py-1 text-[9px] uppercase tracking-widest font-bold rounded-sm border border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 disabled:opacity-40">
              <Plus size={10} weight="bold" className="inline" /> Charlie
            </button>
            <button onClick={() => onAdd(team, "dakota")} disabled={busy} data-testid={`add-dakota-${team.team_abbr}`}
              className="px-2 py-1 text-[9px] uppercase tracking-widest font-bold rounded-sm border border-[#A855F7]/50 text-[#A855F7] hover:bg-[#A855F7]/10 disabled:opacity-40">
              <Plus size={10} weight="bold" className="inline" /> Dakota
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/[0.04] bg-white/[0.02]"><td colSpan={8} className="px-4 py-4">
          <BigRoad title={team.team_name} subtitle={`${team.team_abbr} · ${sport.toUpperCase()}`} results={team.results} mode={mode} analystFilter={analystFilter} />
        </td></tr>
      )}
    </>
  );
}

export default function StrategyLab() {
  const [sport, setSport] = useState(() => lsGet(LS_KEYS.sport, "mlb"));
  const [mode, setMode] = useState(() => lsGet(LS_KEYS.mode, "diff"));
  const [analystFilter, setAnalystFilter] = useState(() => lsGet(LS_KEYS.analyst, "both")); // "both" | "charlie" | "dakota"
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locking, setLocking] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validatorOpen, setValidatorOpen] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);

  // Persist filter state across sessions.
  useEffect(() => { lsSet(LS_KEYS.sport, sport); }, [sport]);
  useEffect(() => { lsSet(LS_KEYS.mode, mode); }, [mode]);
  useEffect(() => { lsSet(LS_KEYS.analyst, analystFilter); }, [analystFilter]);

  const handleValidate = useCallback(async () => {
    if (validating) return;
    setValidating(true);
    try {
      const { data } = await api.get("/admin/strategy/validate-picks");
      setValidation(data);
      setValidatorOpen(true);
      const m = data.mismatch_count || 0;
      const u = data.unknown_count || 0;
      if (m === 0 && u === 0) {
        toast.success(`Audit clean · ${data.match_count} of ${data.pending_count} pending picks verified`);
      } else {
        toast.warning(`Audit flagged ${m + u} pending pick(s)`, {
          description: `${m} mismatched analyst · ${u} no-trigger · ${data.match_count} matched`,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  }, [validating]);

  const handleDiagnose = useCallback(async () => {
    if (diagnosing) return;
    setDiagnosing(true);
    try {
      // Run BOTH sport diagnostics in parallel so user sees full picture.
      const [mlb, wnba] = await Promise.all([
        api.get("/admin/strategy/diagnose-slate?sport=mlb"),
        api.get("/admin/strategy/diagnose-slate?sport=wnba"),
      ]);
      setDiagnosis({ mlb: mlb.data, wnba: wnba.data });
      setDiagOpen(true);
      const mFires = (mlb.data.teams || []).filter((t) => t.verdict?.fires).length;
      const wFires = (wnba.data.teams || []).filter((t) => t.verdict?.fires).length;
      toast.success(`Slate diagnosed · MLB ${mFires} firing · WNBA ${wFires} firing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnose failed");
    } finally {
      setDiagnosing(false);
    }
  }, [diagnosing]);

  // Helper: extract "selected" view summary for both analysts from a matrix payload.
  const grabSelected = (m) => {
    const v = m?.views?.selected || {};
    return {
      charlie: { winRate: v.charlie?.summary?.win_rate || 0, profit: v.charlie?.summary?.profit || 0 },
      dakota: { winRate: v.dakota?.summary?.win_rate || 0, profit: v.dakota?.summary?.profit || 0 },
    };
  };

  const fetchMatrix = useCallback(async () => {
    try {
      const res = sport === "mlb"
        ? await api.get(`/admin/strategy/comparison-matrix?season=2026`)
        : await api.get(`/admin/strategy/wnba-matrix?season=2025`);
      return res.data;
    } catch { return null; }
  }, [sport]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: roadData }, freshMatrix] = await Promise.all([
        api.get(`/admin/strategy/big-road?sport=${sport}&mode=${mode}&expanded=true`),
        fetchMatrix(),
      ]);
      setData(roadData);
      setMatrix(freshMatrix);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lab load failed");
    } finally { setLoading(false); }
  }, [sport, mode, fetchMatrix]);

  useEffect(() => { load(); }, [load]);

  const allTeams = data?.tracked_teams || [];
  const shortlisted = allTeams.filter((t) => t.in_charlie || t.in_dakota);
  const universe = useMemo(() => {
    const non = allTeams.filter((t) => !(t.in_charlie || t.in_dakota));
    return [...non].sort((a, b) => {
      const ra = analystRecord(a, analystFilter);
      const rb = analystRecord(b, analystFilter);
      const da = dollarsFor(ra.theoW, ra.theoL) - dollarsFor(ra.actualW, ra.actualL);
      const db = dollarsFor(rb.theoW, rb.theoL) - dollarsFor(rb.actualW, rb.actualL);
      return db - da;
    });
  }, [allTeams, analystFilter]);

  const handleAdd = async (team, analyst) => {
    if (busy) return; setBusy(true);
    const before = grabSelected(matrix);
    try {
      if (sport === "mlb") {
        const strategy = analyst === "charlie" ? "iso" : "wc";
        await api.post("/admin/strategy/teams", { strategy, action: "add", team_id: team.team_id, effective_date: TODAY_ISO() });
      } else {
        await api.post("/admin/strategy/wnba-teams", { strategy: analyst, action: "add", team_abbr: team.team_abbr, effective_date: TODAY_ISO() });
      }
      const freshMatrix = await fetchMatrix();
      const after = grabSelected(freshMatrix);
      const a = analyst === "charlie" ? "charlie" : "dakota";
      const wrDelta = ((after[a].winRate - before[a].winRate) * 100);
      const pDelta = after[a].profit - before[a].profit;
      const deltaTxt = `Selected ${a === "charlie" ? "Charlie" : "Dakota"}: ${(before[a].winRate * 100).toFixed(0)}% → ${(after[a].winRate * 100).toFixed(0)}% (${wrDelta >= 0 ? "+" : ""}${wrDelta.toFixed(0)}pp) · ${fmtMoney(pDelta)}`;
      toast.success(`${team.team_abbr} added to ${a === "charlie" ? "Charlie" : "Dakota"} shortlist`, { description: deltaTxt });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const handleRemove = async (team) => {
    if (busy) return; setBusy(true);
    const before = grabSelected(matrix);
    try {
      if (sport === "mlb") {
        if (team.in_charlie) await api.post("/admin/strategy/teams", { strategy: "iso", action: "remove", team_id: team.team_id, effective_date: TODAY_ISO() });
        if (team.in_dakota) await api.post("/admin/strategy/teams", { strategy: "wc", action: "remove", team_id: team.team_id, effective_date: TODAY_ISO() });
      } else {
        if (team.in_charlie) await api.post("/admin/strategy/wnba-teams", { strategy: "charlie", action: "remove", team_abbr: team.team_abbr, effective_date: TODAY_ISO() });
        if (team.in_dakota) await api.post("/admin/strategy/wnba-teams", { strategy: "dakota", action: "remove", team_abbr: team.team_abbr, effective_date: TODAY_ISO() });
      }
      const freshMatrix = await fetchMatrix();
      const after = grabSelected(freshMatrix);
      const cWr = ((after.charlie.winRate - before.charlie.winRate) * 100);
      const dWr = ((after.dakota.winRate - before.dakota.winRate) * 100);
      const desc = `Charlie ${(before.charlie.winRate * 100).toFixed(0)}% → ${(after.charlie.winRate * 100).toFixed(0)}% (${cWr >= 0 ? "+" : ""}${cWr.toFixed(0)}pp) · Dakota ${(before.dakota.winRate * 100).toFixed(0)}% → ${(after.dakota.winRate * 100).toFixed(0)}% (${dWr >= 0 ? "+" : ""}${dWr.toFixed(0)}pp)`;
      toast.success(`${team.team_abbr} removed from shortlist`, { description: desc });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  const handleSendWeeklySummary = () => {
    if (sendingSummary) return;
    const runSend = async () => {
      setSendingSummary(true);
      try {
        const { data } = await api.post("/admin/picks/weekly-summary", { window_days: 7 });
        toast.success("Weekly summary emailed", {
          description: `Sent to ${data.to} · ${data.report?.sections?.length || 0} sport section(s)`,
        });
      } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
      finally { setSendingSummary(false); }
    };
    toast("Email weekly Lab summary to admin?", {
      description: "Recaps last 7 days of skipped picks ($ left on the table) + top 3 shortlist candidates per analyst.",
      duration: 8000,
      action: { label: "Send now", onClick: () => { runSend(); } },
      cancel: { label: "Cancel", onClick: () => { /* noop */ } },
    });
  };

  const handlePromote = () => {
    if (locking) return;
    const label = sport === "mlb" ? "MLB" : "WNBA";
    const runLock = async () => {
      setLocking(true);
      try {
        const path = sport === "mlb" ? "/admin/picks/lock-now" : "/admin/wnba/picks/lock-now";
        const { data: res } = await api.post(path, { target_date: TODAY_ISO() });
        const created = res?.newly_locked ?? 0;
        const skipped = res?.already_locked ?? 0;
        toast.success(`${label} slate locked`, {
          description: `${created} new pick(s) locked${skipped ? ` · ${skipped} already locked (idempotent skip)` : ""}`,
        });
        await load();
      } catch (e) { toast.error(e.response?.data?.detail || "Lock failed"); }
      finally { setLocking(false); }
    };
    toast(`Force-lock today's ${label} slate?`, {
      description: "Regenerates picks from your current shortlist. Idempotent — existing picks are kept.",
      duration: 8000,
      action: { label: "Lock now", onClick: () => { runLock(); } },
      cancel: { label: "Cancel", onClick: () => { /* noop */ } },
    });
  };

  // Aggregate matrix views (Universe / Above .500 / Selected) for the docked footer.
  const agg = (() => {
    if (!matrix) return null;
    const views = matrix.views || {};
    const grab = (view, analyst) => {
      const v = views[view]?.[analyst]?.summary || {};
      return { winRate: v.win_rate || 0, profit: v.profit || 0, picks: v.picks || 0 };
    };
    return {
      universe: { charlie: grab("universe", "charlie"), dakota: grab("universe", "dakota") },
      above500: { charlie: grab("above_500", "charlie"), dakota: grab("above_500", "dakota") },
      selected: { charlie: grab("selected", "charlie"), dakota: grab("selected", "dakota") },
    };
  })();

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10 pb-28" data-testid="lab-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Admin · Strategy Lab</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">STRATEGY LAB</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl">
          Every team, every analyst, in one screen. Pinned Big Roads for your shortlisted teams stay visible; browse the universe table below sorted by money-left-on-the-table to find new additions. Aggregate metrics dock at the bottom.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <PillGroup>
          <Pill active={sport === "mlb"} onClick={() => setSport("mlb")} bg="#007AFF" txt="#fff" dimTxt="#5BA8FF" data-testid="lab-sport-mlb"><Baseball size={14} weight="fill" /> MLB</Pill>
          <Pill active={sport === "wnba"} onClick={() => setSport("wnba")} bg="#FF6B35" txt="#fff" dimTxt="#FFA985" data-testid="lab-sport-wnba"><Basketball size={14} weight="fill" /> WNBA</Pill>
        </PillGroup>
        <PillGroup>
          <Pill active={mode === "actual"} onClick={() => setMode("actual")} bg="#22C55E" txt="#000" dimTxt="#86EFAC" data-testid="lab-mode-actual"><Trophy size={13} weight="fill" /> Actual</Pill>
          <Pill active={mode === "theoretical"} onClick={() => setMode("theoretical")} bg="#A855F7" txt="#fff" dimTxt="#D8B4FE" data-testid="lab-mode-theoretical"><Lightning size={13} weight="fill" /> Theoretical</Pill>
          <Pill active={mode === "diff"} onClick={() => setMode("diff")} bg="#FF6B35" txt="#fff" dimTxt="#FFA985" data-testid="lab-mode-diff"><GitDiff size={13} weight="fill" /> Diff</Pill>
        </PillGroup>
        <PillGroup>
          <Pill active={analystFilter === "both"} onClick={() => setAnalystFilter("both")} bg="#FFFFFF" txt="#000" dimTxt="#D4D4D8" data-testid="lab-analyst-both">Both</Pill>
          <Pill active={analystFilter === "charlie"} onClick={() => setAnalystFilter("charlie")} bg="#007AFF" txt="#fff" dimTxt="#5BA8FF" data-testid="lab-analyst-charlie">Charlie only</Pill>
          <Pill active={analystFilter === "dakota"} onClick={() => setAnalystFilter("dakota")} bg="#A855F7" txt="#fff" dimTxt="#D8B4FE" data-testid="lab-analyst-dakota">Dakota only</Pill>
        </PillGroup>
        <button onClick={handleValidate} disabled={validating || loading} data-testid="lab-validate-picks"
          title="Recompute Charlie + Dakota triggers from raw game data and cross-check every PENDING locked pick"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 border border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE]/10 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40">
          <ShieldCheck size={12} weight="fill" /> {validating ? "Validating…" : "Validate picks"}
        </button>
        <button onClick={handleDiagnose} disabled={diagnosing || loading} data-testid="lab-diagnose-slate"
          title="Show per-team trigger status across MLB + WNBA — answers 'why didn't team X get picked today?'"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40">
          <Compass size={12} weight="fill" /> {diagnosing ? "Diagnosing…" : "Diagnose slate"}
        </button>
        <button onClick={handlePromote} disabled={locking || loading} data-testid="lab-promote-slate"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40">
          <Lock size={12} weight="fill" /> {locking ? "Locking…" : "Promote shortlist → today's slate"}
        </button>
        <button onClick={handleSendWeeklySummary} disabled={sendingSummary || loading} data-testid="lab-weekly-email"
          title="Email a weekly recap of skipped picks + shortlist candidates to admin"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40">
          <EnvelopeSimple size={12} weight="fill" /> {sendingSummary ? "Sending…" : "Email weekly recap"}
        </button>
        <button onClick={load} disabled={loading} data-testid="lab-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40">
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Pinned shortlisted big roads */}
      <div className="mb-3 text-[11px] uppercase tracking-[0.3em] text-[#F59E0B] flex items-center gap-2"><Star size={13} weight="fill" /> Pinned · Your Shortlist · {shortlisted.length} {shortlisted.length === 1 ? "team" : "teams"}</div>
      {shortlisted.length === 0 ? (
        <div className="bg-[#121212] border border-dashed border-white/10 rounded-sm p-8 text-center text-xs text-zinc-500 italic mb-8">
          No teams shortlisted yet. Add teams from the universe table below.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 mb-8">
          {shortlisted.map((t) => <PinnedTile key={`pin-${t.team_id}`} team={t} sport={sport} onRemove={handleRemove} busy={busy} mode={mode} analystFilter={analystFilter} />)}
        </div>
      )}

      {/* Universe table */}
      <div className="mb-3 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
        Universe · {universe.length} teams · sorted by money left on the table (high → low)
        {analystFilter !== "both" && <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-sm" style={{ background: analystFilter === "charlie" ? "#007AFF22" : "#A855F722", color: analystFilter === "charlie" ? "#5BA8FF" : "#D8B4FE" }}>
          {analystFilter === "charlie" ? "Charlie only" : "Dakota only"}
        </span>}
      </div>
      <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid="lab-universe-table">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <th></th>
              <th className="text-left px-2 py-2.5">Team</th>
              <th className="text-left px-2 py-2.5">Season</th>
              <th className="text-left px-2 py-2.5">Actual{analystFilter !== "both" && ` · ${analystFilter === "charlie" ? "C" : "D"}`}</th>
              <th className="text-left px-2 py-2.5">Theoretical{analystFilter !== "both" && ` · ${analystFilter === "charlie" ? "C" : "D"}`}</th>
              <th className="text-right px-2 py-2.5">$ Left ▼</th>
              <th className="text-left px-2 py-2.5">Last 20</th>
              <th className="text-right px-2 py-2.5">Shortlist</th>
            </tr>
          </thead>
          <tbody>
            {universe.map((t) => (
              <UniverseRow key={`u-${t.team_id}`} team={t} sport={sport}
                expanded={expandedKey === t.team_id}
                onToggle={() => setExpandedKey(expandedKey === t.team_id ? null : t.team_id)}
                onAdd={handleAdd} busy={busy} mode={mode} analystFilter={analystFilter} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Sticky aggregate footer */}
      {agg && (
        <div className="fixed left-0 right-0 bottom-0 bg-[#0a0a0a]/95 backdrop-blur border-t-2 border-white/15 z-40 py-2.5 px-6" data-testid="lab-agg-footer">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-5 items-center text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Aggregate · {sport.toUpperCase()}</div>
              <div className="text-[13px] font-bold">
                {analystFilter === "dakota" ? null : <span className="text-[#007AFF]">Charlie</span>}
                {analystFilter === "both" && <span className="text-zinc-600"> · </span>}
                {analystFilter === "charlie" ? null : <span className="text-[#A855F7]">Dakota</span>}
                {analystFilter !== "both" && <span className="ml-2 text-[10px] text-zinc-500 uppercase tracking-widest"> only</span>}
              </div>
            </div>
            {["universe", "above500", "selected"].map((view) => (
              <div key={view} data-testid={`lab-agg-${view}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{view === "universe" ? "A. Universe" : view === "above500" ? "B. Above .500" : "C. Selected"}</div>
                {analystFilter !== "dakota" && (
                  <div className="font-mono-d text-[11px] flex justify-between"><span className="text-[#007AFF]">Charlie</span><span style={{ color: moneyColor(agg[view].charlie.profit) }}>{(agg[view].charlie.winRate * 100).toFixed(0)}% · {fmtMoney(agg[view].charlie.profit)}</span></div>
                )}
                {analystFilter !== "charlie" && (
                  <div className="font-mono-d text-[11px] flex justify-between"><span className="text-[#A855F7]">Dakota</span><span style={{ color: moneyColor(agg[view].dakota.profit) }}>{(agg[view].dakota.winRate * 100).toFixed(0)}% · {fmtMoney(agg[view].dakota.profit)}</span></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation drawer */}
      {validatorOpen && validation && (
        <ValidatorDrawer data={validation} onClose={() => setValidatorOpen(false)} />
      )}

      {/* Diagnosis drawer */}
      {diagOpen && diagnosis && (
        <DiagnosisDrawer data={diagnosis} onClose={() => setDiagOpen(false)} onRefresh={handleDiagnose} />
      )}
    </div>
  );
}

function DiagnosisDrawer({ data, onClose, onRefresh }) {
  const [sportTab, setSportTab] = useState("mlb");
  const [applyingId, setApplyingId] = useState(null);
  const cur = data[sportTab] || { teams: [] };
  const fires = (cur.teams || []).filter((t) => t.verdict?.fires);
  const idle = (cur.teams || []).filter((t) => !t.verdict?.fires);

  const handleApplyNow = useCallback(async (changeId, teamAbbr) => {
    if (applyingId) return;
    setApplyingId(changeId);
    try {
      // Dispatch to the correct sport's endpoint. MLB lives under
      // /admin/strategy/teams/{id}/apply-now, WNBA under /admin/strategy/wnba-teams/...
      const path = sportTab === "wnba"
        ? `/admin/strategy/wnba-teams/${changeId}/apply-now`
        : `/admin/strategy/teams/${changeId}/apply-now`;
      const { data: r } = await api.post(path);
      const newly = r.relocked?.newly_locked || 0;
      toast.success(`${teamAbbr} applied · effective now`, {
        description: newly > 0 ? `Re-locked ${newly} new pick(s) for today.` : "Re-lock ran. Refresh to see updated diagnostic.",
      });
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplyingId(null);
    }
  }, [applyingId, onRefresh, sportTab]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="lab-diagnosis-drawer" onClick={onClose}>
      <div className="bg-[#0a0a0a] border-2 border-[#F59E0B]/40 rounded-sm max-w-[1280px] w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] flex items-center gap-1.5">
              <Compass size={12} weight="fill" /> Slate Diagnostic
            </div>
            <h2 className="font-display text-3xl leading-none mt-1">WHY DIDN&apos;T TEAM X PICK?</h2>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Per-team trigger status for target date {cur.target_date} · re-runs simulator from raw game data
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="diagnosis-close"><X size={20} /></button>
        </div>

        {/* Sport tabs */}
        <div className="flex border-b border-white/10">
          {["mlb", "wnba"].map((s) => (
            <button
              key={s}
              onClick={() => setSportTab(s)}
              data-testid={`diag-tab-${s}`}
              className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold border-b-2 transition-colors ${
                sportTab === s ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {s} · {(data[s]?.teams || []).filter((t) => t.verdict?.fires).length} firing
            </button>
          ))}
        </div>

        {/* Active shortlists */}
        <div className="px-5 py-3 border-b border-white/10 text-[11px] text-zinc-400 flex flex-wrap gap-4">
          <div>
            <span className="text-[#007AFF] font-bold uppercase tracking-widest mr-2">Charlie</span>
            <span className="font-mono-d">{((cur.active_shortlists?.iso ?? cur.active_shortlists?.charlie) || []).join(", ") || "—"}</span>
          </div>
          <div>
            <span className="text-[#A855F7] font-bold uppercase tracking-widest mr-2">Dakota</span>
            <span className="font-mono-d">{((cur.active_shortlists?.wc ?? cur.active_shortlists?.dakota) || []).join(", ") || "—"}</span>
          </div>
          {cur.games_scheduled_today != null && (
            <div>
              <span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-1">Games today</span>
              <span className="font-mono-d text-zinc-200">{cur.games_scheduled_today}</span>
            </div>
          )}
        </div>

        {/* Future-dated shortlist changes warning — these are saved in DB but
            won't take effect until their effective_date. Blocks today's locks. */}
        {(cur.pending_shortlist_changes || []).length > 0 && (
          <div className="px-5 py-3 border-b border-[#F59E0B]/30 bg-[#F59E0B]/5" data-testid="diag-future-warnings">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold mb-2 flex items-center gap-1.5">
              <WarningCircle size={12} weight="fill" /> {cur.pending_shortlist_changes.length} future-dated shortlist change{cur.pending_shortlist_changes.length === 1 ? "" : "s"} — won&apos;t apply to today&apos;s lock
            </div>
            <div className="space-y-1.5">
              {cur.pending_shortlist_changes.map((c, i) => (
                <div key={c.id || i} className="flex items-center justify-between gap-3 text-[11px] py-1 px-2 rounded-sm bg-black/30">
                  <div className="font-mono-d text-zinc-300">
                    <span className="text-[#F59E0B] font-bold">{c.action.toUpperCase()}</span>
                    {" "}{c.strategy} <strong>{c.team_abbr}</strong>
                    {" "}<span className="text-zinc-500">eff {c.effective_date} (target {cur.target_date})</span>
                  </div>
                  {c.id ? (
                    <button
                      onClick={() => handleApplyNow(c.id, c.team_abbr)}
                      disabled={applyingId === c.id}
                      data-testid={`apply-now-${c.team_abbr}`}
                      className="px-2.5 py-1 border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 text-[10px] uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40"
                    >
                      {applyingId === c.id ? "Applying…" : "Apply now"}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2 italic">
              Click Apply now to rewrite effective_date to today and re-lock today&apos;s slate immediately.
            </div>
          </div>
        )}

        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2.5" data-testid="diagnosis-team-list">
          {cur.note ? (
            <div className="text-center text-zinc-500 italic py-12">{cur.note}</div>
          ) : (cur.teams || []).length === 0 ? (
            <div className="text-center text-zinc-500 italic py-12">No teams in either shortlist.</div>
          ) : (
            <>
              {fires.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-[#22C55E] mb-1.5">⚡ Firing today</div>
              )}
              {fires.map((t) => <DiagRow key={t.team_id || t.team_abbr} t={t} sport={sportTab} startOpen={true} />)}
              {idle.length > 0 && fires.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-4 mb-1.5">🟡 Idle</div>
              )}
              {idle.map((t) => <DiagRow key={t.team_id || t.team_abbr} t={t} sport={sportTab} startOpen={false} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DiagRow({ t, sport, startOpen }) {
  const [open, setOpen] = useState(!!startOpen);
  const v = t.verdict || {};
  const accent = v.fires ? "#22C55E" : "#71717A";
  return (
    <div className="border rounded-sm" style={{ borderColor: `${accent}55` }} data-testid={`diag-row-${t.team_abbr}`}>
      <div className="p-3 flex items-center justify-between gap-4 flex-wrap cursor-pointer hover:bg-white/[0.025]" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          {v.fires ? <Check size={16} weight="bold" style={{ color: accent }} /> : <WarningCircle size={16} weight="fill" className="text-zinc-500" />}
          <div>
            <div className="text-sm font-bold">{t.team_abbr} {t.team ? <span className="text-zinc-500 text-xs font-normal ml-1">{t.team}</span> : null}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t.in_charlie_shortlist ? "Charlie" : ""}{t.in_charlie_shortlist && t.in_dakota_shortlist ? " · " : ""}{t.in_dakota_shortlist ? "Dakota" : ""}
              {sport === "wnba" ? (t.plays_today ? " · plays today" : " · IDLE today") : ""}
            </div>
          </div>
        </div>
        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accent }}>
          {v.fires ? `→ ${v.analyst}` : "no pick"}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] pt-3">
          <div className="bg-white/[0.02] p-3 rounded-sm">
            <div className="text-[#007AFF] font-bold uppercase tracking-widest text-[10px] mb-1.5">Charlie (iso-1L)</div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: t.iso_signal?.fires ? "#22C55E22" : "#3f3f46", color: t.iso_signal?.fires ? "#22C55E" : "#a1a1aa" }}>
                {t.iso_signal?.fires ? "Trigger fires" : "No trigger"}
              </span>
            </div>
            <div className="text-zinc-400 leading-snug">{t.iso_signal?.reason}</div>
          </div>
          <div className="bg-white/[0.02] p-3 rounded-sm">
            <div className="text-[#A855F7] font-bold uppercase tracking-widest text-[10px] mb-1.5">Dakota (W1-after-L)</div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: t.wc_signal?.fires ? "#22C55E22" : "#3f3f46", color: t.wc_signal?.fires ? "#22C55E" : "#a1a1aa" }}>
                {t.wc_signal?.fires ? "Trigger fires" : "No trigger"}
              </span>
            </div>
            <div className="text-zinc-400 leading-snug">{t.wc_signal?.reason}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Last 14 games (newest right)</div>
            <div className="flex gap-1 flex-wrap">
              {(t.last_14_games || t.last_8_games || []).map((h, i) => (
                <div key={i} className="flex flex-col items-center gap-0.5" title={`${h.date} · ${h.result} vs ${h.opponent} · ${h.streak}`}>
                  <span className={`inline-block w-5 h-5 rounded-full text-[8px] font-bold text-white flex items-center justify-center ${h.result === "W" ? "bg-red-600" : "bg-blue-500"}`}>{h.result}</span>
                  <span className="text-[8px] text-zinc-600 font-mono-d">{(h.date || "").slice(5)}</span>
                </div>
              ))}
              {(t.last_14_games || t.last_8_games || []).length === 0 && <span className="text-zinc-600 italic">no prior games</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ValidatorDrawer({ data, onClose }) {
  const audit = data.audit || [];
  const pending = data.pending_count || 0;
  const matched = data.match_count || 0;
  const mismatched = data.mismatch_count || 0;
  const unknown = data.unknown_count || 0;
  const stamp = (data.as_of || "").slice(0, 19).replace("T", " ") + " UTC";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="lab-validator-drawer" onClick={onClose}>
      <div className="bg-[#0a0a0a] border-2 border-[#22D3EE]/40 rounded-sm max-w-[1280px] w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#22D3EE] flex items-center gap-1.5">
              <ShieldCheck size={12} weight="fill" /> Pick Data Validator
            </div>
            <h2 className="font-display text-3xl leading-none mt-1">PENDING PICKS AUDIT</h2>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Re-ran the simulator independently and cross-checked every PENDING locked pick · {stamp}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="validator-close"><X size={20} /></button>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border-b border-white/10">
          <div className="bg-[#0a0a0a] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pending</div>
            <div className="font-display text-2xl" data-testid="validator-pending">{pending}</div>
          </div>
          <div className="bg-[#0a0a0a] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Matched</div>
            <div className="font-display text-2xl text-[#22C55E]" data-testid="validator-matched">{matched}</div>
          </div>
          <div className="bg-[#0a0a0a] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Mismatched</div>
            <div className="font-display text-2xl" style={{ color: mismatched ? "#FF3B30" : "#a1a1aa" }} data-testid="validator-mismatched">{mismatched}</div>
          </div>
          <div className="bg-[#0a0a0a] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">No trigger</div>
            <div className="font-display text-2xl" style={{ color: unknown ? "#F59E0B" : "#a1a1aa" }} data-testid="validator-unknown">{unknown}</div>
          </div>
        </div>

        {/* Active shortlists */}
        <div className="px-5 py-3 border-b border-white/10 text-[11px] text-zinc-400 flex flex-wrap gap-4">
          <div>
            <span className="text-[#007AFF] font-bold uppercase tracking-widest mr-2">Charlie iso</span>
            <span className="font-mono-d">{(data.active_shortlists?.charlie_iso || []).join(", ") || "—"}</span>
          </div>
          <div>
            <span className="text-[#A855F7] font-bold uppercase tracking-widest mr-2">Dakota WC</span>
            <span className="font-mono-d">{(data.active_shortlists?.dakota_wc || []).join(", ") || "—"}</span>
          </div>
        </div>

        {/* Audit table */}
        <div className="p-4 max-h-[60vh] overflow-y-auto" data-testid="validator-audit-list">
          {audit.length === 0 ? (
            <div className="text-center text-zinc-500 italic py-12">No PENDING locked picks to audit.</div>
          ) : (
            <div className="space-y-2.5">
              {audit.map((r) => <AuditRow key={r.lock_id} r={r} />)}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/10 text-[10px] text-zinc-500 italic">
          {(data.notes || []).map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      </div>
    </div>
  );
}

function AuditRow({ r }) {
  const matched = r.match === true;
  const noTrigger = r.expected_analyst == null;
  const mismatched = r.match === false && r.expected_analyst;
  const accent = matched ? "#22C55E" : mismatched ? "#FF3B30" : "#F59E0B";
  const [open, setOpen] = useState(mismatched || noTrigger);
  return (
    <div className="border rounded-sm" style={{ borderColor: `${accent}55` }} data-testid={`audit-row-${r.team_abbr}`}>
      <div className="p-3 flex items-center justify-between gap-4 flex-wrap cursor-pointer hover:bg-white/[0.025]" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          {matched ? <Check size={16} weight="bold" style={{ color: accent }} /> : <WarningCircle size={16} weight="fill" style={{ color: accent }} />}
          <div>
            <div className="text-sm font-bold">{r.team_abbr} @ {r.opponent_abbr || "—"}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.target_date} · gamePk {r.target_gamePk}</div>
          </div>
        </div>
        <div className="flex items-center gap-5 text-[11px] flex-wrap">
          <div>
            <span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1.5">Locked</span>
            <span className="font-bold" style={{ color: r.locked_analyst === "Charlie" ? "#007AFF" : "#A855F7" }}>{r.locked_analyst}</span>
          </div>
          <div>
            <span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1.5">Expected</span>
            <span className="font-bold" style={{ color: r.expected_analyst === "Charlie" ? "#007AFF" : r.expected_analyst === "Dakota" ? "#A855F7" : "#a1a1aa" }}>{r.expected_analyst || "— none —"}</span>
          </div>
          <div className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold" style={{ background: `${accent}22`, color: accent }}>
            {matched ? "Match" : mismatched ? "Mismatch" : "No trigger"}
          </div>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px]">
          <div className="bg-white/[0.02] p-3 rounded-sm">
            <div className="text-[#007AFF] font-bold uppercase tracking-widest text-[10px] mb-1.5">Charlie (iso-1L)</div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: r.in_charlie_shortlist ? "#007AFF22" : "#3f3f46", color: r.in_charlie_shortlist ? "#5BA8FF" : "#a1a1aa" }}>
                {r.in_charlie_shortlist ? "In shortlist" : "Not shortlisted"}
              </span>
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: r.iso_signal?.fires ? "#22C55E22" : "#3f3f46", color: r.iso_signal?.fires ? "#22C55E" : "#a1a1aa" }}>
                {r.iso_signal?.fires ? "Trigger fires" : "No trigger"}
              </span>
            </div>
            <div className="text-zinc-400 leading-snug">{r.iso_signal?.reason}</div>
          </div>
          <div className="bg-white/[0.02] p-3 rounded-sm">
            <div className="text-[#A855F7] font-bold uppercase tracking-widest text-[10px] mb-1.5">Dakota (W1-after-L)</div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: r.in_dakota_shortlist ? "#A855F722" : "#3f3f46", color: r.in_dakota_shortlist ? "#D8B4FE" : "#a1a1aa" }}>
                {r.in_dakota_shortlist ? "In shortlist" : "Not shortlisted"}
              </span>
              <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold" style={{ background: r.wc_signal?.fires ? "#22C55E22" : "#3f3f46", color: r.wc_signal?.fires ? "#22C55E" : "#a1a1aa" }}>
                {r.wc_signal?.fires ? "Trigger fires" : "No trigger"}
              </span>
            </div>
            <div className="text-zinc-400 leading-snug">{r.wc_signal?.reason}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Last 20 games (newest right)</div>
            <div className="flex gap-1 flex-wrap">
              {(r.history_last_20 || []).map((h, i) => (
                <div key={i} className="flex flex-col items-center gap-0.5" title={`${h.date} · ${h.result} vs ${h.opponent} · ${h.score} · streak ${h.streak_type}${h.streak_len} · in-cycle iso ${h.in_cycle_iso_1L}`}>
                  <span className={`inline-block w-4 h-4 rounded-full text-[8px] font-bold text-white flex items-center justify-center ${h.result === "W" ? "bg-red-600" : "bg-blue-500"}`}>{h.result}</span>
                  <span className="text-[8px] text-zinc-600 font-mono-d">{(h.date || "").slice(5)}</span>
                </div>
              ))}
              {(r.history_last_20 || []).length === 0 && <span className="text-zinc-600 italic">no prior games found</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
